import {
  ArrowLeft,
  Bot,
  BrainCircuit,
  ChevronRight,
  Clock3,
  Workflow as WorkflowIcon,
  Sparkles,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { cn } from "../../lib/utils";

const modes = [
  {
    id: "single",
    title: "Single Agent",
    description: "Generate one production-ready prompt, then review and publish it in Agent Studio.",
    icon: Bot,
    enabled: true,
  },
  {
    id: "thinker-talker",
    title: "Thinker-Talker",
    description: "Generate separate fast-response and deep-reasoning paths.",
    icon: BrainCircuit,
    enabled: false,
  },
  {
    id: "workflow",
    title: "Workflow",
    description: "Generate a routed workflow across multiple agents.",
    icon: WorkflowIcon,
    enabled: false,
  },
] as const;

export function AutoBuilderModePage() {
  const navigate = useNavigate();
  const location = useLocation();

  const selectMode = (modeId: string, enabled: boolean) => {
    if (!enabled || modeId !== "single") return;
    navigate(`/home/autobuilder/single/new${location.search}`);
  };

  return (
    <div className="flex min-h-full flex-1 flex-col bg-page">
      <header className="flex h-16 shrink-0 items-center gap-3 border-b border-line bg-elevated px-5">
        <button
          type="button"
          onClick={() => navigate("/")}
          aria-label="Back to agents"
          className="rounded-md p-2 text-ink-3 hover:bg-soft hover:text-ink"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div>
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-brand" />
            <h1 className="text-sm font-bold text-ink">AutoBuilder</h1>
          </div>
          <p className="mt-0.5 text-xs text-ink-3">Choose the agent architecture to generate.</p>
        </div>
      </header>

      <main className="flex flex-1 items-center justify-center p-6">
        <div className="w-full max-w-4xl">
          <div className="mb-6">
            <h2 className="text-sm font-semibold text-ink">What do you want to build?</h2>
            
          </div>

          <div className="grid overflow-hidden rounded-md border border-line bg-line md:grid-cols-3 md:gap-px">
            {modes.map((mode) => {
              const Icon = mode.icon;
              return (
                <button
                  key={mode.id}
                  type="button"
                  disabled={!mode.enabled}
                  onClick={() => selectMode(mode.id, mode.enabled)}
                  className={cn(
                    "group min-h-56 bg-elevated p-5 text-left transition-colors",
                    mode.enabled
                      ? "hover:bg-soft"
                      : "cursor-not-allowed opacity-60",
                  )}
                >
                  <div className="flex items-start justify-between gap-3">
                    <span className={cn(
                      "flex h-10 w-10 items-center justify-center rounded-md",
                      mode.enabled ? "bg-brand-soft text-brand" : "bg-soft text-ink-3",
                    )}>
                      <Icon className="h-5 w-5" />
                    </span>
                    <span className={cn(
                      "inline-flex items-center gap-1 rounded px-2 py-1 text-[10px] font-bold uppercase",
                      mode.enabled ? "bg-success-soft text-success" : "bg-soft text-ink-3",
                    )}>
                      {!mode.enabled && <Clock3 className="h-3 w-3" />}
                      {mode.enabled ? "Available" : "Coming soon"}
                    </span>
                  </div>
                  <h3 className="mt-5 text-base font-bold text-ink">{mode.title}</h3>
                  <p className="mt-2 min-h-16 text-sm leading-6 text-ink-2">{mode.description}</p>
                  {mode.enabled && (
                    <span className="mt-5 inline-flex items-center gap-1 text-xs font-bold text-brand">
                      Continue
                      <ChevronRight className="h-3.5 w-3.5" />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </main>
    </div>
  );
}
